import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* Studio review queue for New Citizen global-ban appeals. Players file these from
   the public /appeal kiosk; approving one lifts the global ban (digest deactivated,
   nc_banned cleared) and the player sees the verdict the next time they open the kiosk. */

const STATUS_CLS = {
  pending: "text-amber-400 border-amber-500/40",
  approved: "text-emerald-400 border-emerald-500/40",
  denied: "text-red-400 border-red-500/40",
};

export default function AdminAppeals() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ appeals: [], counts: {} });
  const [notes, setNotes] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/nc/studio/appeals", { params: { status: tab } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const review = async (id, decision) => {
    setBusy(id + decision);
    try {
      const r = await api.post(`/nc/studio/appeals/${id}/review`, { decision, note: notes[id] || "" });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div data-testid="admin-appeals" className="flex flex-col gap-4 max-w-4xl">
      <div className="border border-white/10 bg-[#0a0a0a] p-4">
        <p className="font-mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase flex items-center gap-2">
          <Scale className="w-3.5 h-3.5 text-nc-orange" /> Ban appeals — review queue
        </p>
        <p className="font-mono text-[10px] text-zinc-500 mt-2 leading-relaxed">
          Banned players plead their case at the public <span className="text-zinc-300">/appeal</span> kiosk.
          Approving lifts the global Steam ban instantly; denying starts a 7-day cooldown before they can try again.
        </p>
      </div>

      <div className="flex gap-1 flex-wrap">
        {["pending", "approved", "denied", "all"].map((s) => (
          <button key={s} data-testid={`appeals-tab-${s}`} onClick={() => setTab(s)}
            className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] border transition-colors ${tab === s ? "bg-nc-orange text-black border-nc-orange" : "border-white/15 text-zinc-400 hover:border-white/40"}`}>
            {s}{s !== "all" && data.counts?.[s] != null ? ` (${data.counts[s]})` : ""}
          </button>
        ))}
      </div>

      {loading && <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-nc-orange" /></div>}
      {!loading && data.appeals.length === 0 && (
        <p data-testid="appeals-empty" className="font-mono text-[10px] text-zinc-500 uppercase tracking-[0.25em] py-10 text-center border border-white/10">No {tab} appeals</p>
      )}

      {data.appeals.map((a) => (
        <div key={a.id} data-testid={`appeal-card-${a.id.slice(0, 8)}`} className="border border-white/10 bg-[#0a0a0a] p-5 flex flex-col gap-3">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-bold text-sm text-zinc-100">{a.name}</p>
            <span className="font-mono text-[10px] text-zinc-500">{a.email}</span>
            <span className="font-mono text-[9px] text-zinc-600">ban {a.h}</span>
            <span className="font-mono text-[9px] text-zinc-600">{String(a.created_at).slice(0, 10)}</span>
            <span className={`ml-auto font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS_CLS[a.status] || STATUS_CLS.pending}`}>{a.status}</span>
          </div>
          <div className="border border-red-500/20 bg-red-500/5 px-3.5 py-2.5">
            <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-red-400/80 mb-1">Banned for</p>
            <p className="font-mono text-[11px] text-zinc-300">{a.ban_reason}</p>
          </div>
          <p className="font-mono text-[11px] text-zinc-200 leading-relaxed whitespace-pre-wrap">"{a.text}"</p>
          {a.status === "pending" ? (
            <>
              <textarea data-testid={`appeal-note-${a.id.slice(0, 8)}`} rows={2} value={notes[a.id] || ""}
                onChange={(e) => setNotes((n) => ({ ...n, [a.id]: e.target.value }))}
                placeholder="Review note (shown to the player)…"
                className="w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:border-nc-orange resize-none" />
              <div className="flex gap-2">
                <button data-testid={`appeal-approve-${a.id.slice(0, 8)}`} onClick={() => review(a.id, "approved")} disabled={!!busy}
                  className="px-5 py-2.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-black flex items-center gap-2 disabled:opacity-40"
                  style={{ backgroundColor: "#34d399" }}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve — lift ban
                </button>
                <button data-testid={`appeal-deny-${a.id.slice(0, 8)}`} onClick={() => review(a.id, "denied")} disabled={!!busy}
                  className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-2 disabled:opacity-40 transition-colors">
                  <XCircle className="w-3.5 h-3.5" /> Deny
                </button>
              </div>
            </>
          ) : (
            <p className="font-mono text-[10px] text-zinc-500">
              {a.status} by {a.reviewed_by} on {String(a.reviewed_at).slice(0, 10)}{a.review_note ? ` — "${a.review_note}"` : ""}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
